import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaBell, FaCog, FaSignOutAlt, FaEnvelope, FaExclamationTriangle, FaCheckCircle, FaChevronDown, FaExpandAlt } from 'react-icons/fa';
import styles from '../styles/Topbar.module.css';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { notificationService } from '../services/apiConnectionService';
import {
  getOrCreateParentThread,
  markThreadRead,
  sendThreadMessage,
  subscribeThreadMessages,
  subscribeUserThreads,
  subscribeAllParentThreads
} from '../services/parentMessagingService';

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
};

const roleBadgeColor = (role) => {
  switch (role) {
    case 'administrator': return { bg: '#7c3aed', text: '#fff' };
    case 'manager': return { bg: '#2563eb', text: '#fff' };
    case 'parent': return { bg: '#059669', text: '#fff' };
    default: return { bg: '#4b5563', text: '#fff' };
  }
};

const Topbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [messageDropdownOpen, setMessageDropdownOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [messageDraft, setMessageDraft] = useState('');
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [messageStatus, setMessageStatus] = useState('');
  const [parentProfile, setParentProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [startingConversation, setStartingConversation] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const messageRef = useRef(null);
  const messageButtonRef = useRef(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserInfo = async (user) => {
      if (user) {
        setCurrentUserId(user.uid);
        try {
          // First, try to get user document by document ID (matching auth UID)
          let userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Use the actual data from Firestore, with fallbacks only for missing fields
            setUserInfo({
              ...userData,
              // Only override if the field is missing, not if it's falsy (could be empty string)
              email: userData.email !== undefined ? userData.email : user.email,
              firstName: userData.firstName !== undefined ? userData.firstName : (userData.displayName?.split(' ')[0] || user.email?.split('@')[0] || ''),
              lastName: userData.lastName !== undefined ? userData.lastName : (userData.displayName?.split(' ').slice(1).join(' ') || ''),
              role: userData.role !== undefined ? userData.role : 'user',
              profilePicture: userData.profilePicture !== undefined ? userData.profilePicture : null
            });

            if (userData.role === 'parent') {
              // Keep Topbar parent profile lookup rule-safe and aligned with ParentDashboard.
              if (userData.parentRef) {
                const parentByRef = await getDoc(doc(db, 'parents', userData.parentRef));
                if (parentByRef.exists()) {
                  setParentProfile({ id: parentByRef.id, ...parentByRef.data() });
                } else {
                  setParentProfile(null);
                }
              } else if (userData.parentId) {
                const parentByParentIdQuery = query(
                  collection(db, 'parents'),
                  where('parentId', '==', userData.parentId),
                  limit(1)
                );
                const parentSnapshot = await getDocs(parentByParentIdQuery);
                if (!parentSnapshot.empty) {
                  setParentProfile({ id: parentSnapshot.docs[0].id, ...parentSnapshot.docs[0].data() });
                } else {
                  setParentProfile(null);
                }
              } else {
                setParentProfile(null);
              }
            } else {
              setParentProfile(null);
            }
          } else {
            // If user document doesn't exist, use basic info from auth
            console.warn('User document not found for uid:', user.uid);
            setUserInfo({
              email: user.email,
              displayName: user.displayName || user.email?.split('@')[0],
              firstName: user.displayName?.split(' ')[0] || user.email?.split('@')[0],
              lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
              role: 'user'
            });
            setParentProfile(null);
          }
        } catch (err) {
          console.error('Error fetching user info:', err);
          console.error('Error code:', err.code);
          console.error('Error message:', err.message);
          // Only fall back if it's a permission error, not other errors
          if (err.code === 'permission-denied' || err.message?.includes('Missing or insufficient permissions')) {
            console.warn('Permission denied for user document, using auth user info only');
            setUserInfo({
              email: user.email,
              displayName: user.displayName || user.email?.split('@')[0],
              firstName: user.displayName?.split(' ')[0] || user.email?.split('@')[0],
              lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
              role: 'user'
            });
            setParentProfile(null);
          } else if (err.code === 'unavailable' || err.message?.includes('400')) {
            // Network or other errors - still try to show something
            setUserInfo({
              email: user.email,
              displayName: user.displayName || user.email?.split('@')[0],
              firstName: user.displayName?.split(' ')[0] || user.email?.split('@')[0],
              lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
              role: 'user'
            });
            setParentProfile(null);
          }
          // For other errors, don't set userInfo - let it remain null and show fallback
        }
      } else {
        // User is not logged in
        setCurrentUserId(null);
        setUserInfo(null);
        setParentProfile(null);
      }
    };

    // Fetch immediately if user is already logged in
    fetchUserInfo(auth.currentUser);

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      fetchUserInfo(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
      if (messageRef.current && !messageRef.current.contains(event.target)) {
        setMessageDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Subscribe to notifications
    const unsubscribe = notificationService.subscribe((notification) => {
      setNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep last 50
      setUnreadCount(prev => prev + 1);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) {
      setMessageThreads([]);
      setSelectedThreadId(null);
      setMessageUnreadCount(0);
      return undefined;
    }

    const isStaff = userInfo?.role === 'administrator' || userInfo?.role === 'manager';

    const onThreads = (threads) => {
      setMessageThreads(threads);
      setMessageUnreadCount(
        threads.reduce((sum, thread) => sum + (thread.unreadBy?.[currentUserId] || 0), 0)
      );

      if (!selectedThreadId && threads.length > 0) {
        setSelectedThreadId(threads[0].id);
      } else if (selectedThreadId && !threads.find((thread) => thread.id === selectedThreadId)) {
        setSelectedThreadId(threads[0]?.id || null);
      }
    };

    const unsubscribeThreads = isStaff
      ? subscribeAllParentThreads(onThreads)
      : subscribeUserThreads(currentUserId, onThreads);

    return () => unsubscribeThreads();
  }, [currentUserId, selectedThreadId, userInfo?.role]);

  useEffect(() => {
    if (!selectedThreadId || !currentUserId) {
      setThreadMessages([]);
      return undefined;
    }

    const unsubscribeMessages = subscribeThreadMessages(selectedThreadId, (messages) => {
      setThreadMessages(messages);
    });

    markThreadRead({ threadId: selectedThreadId, userId: currentUserId }).catch(() => {});
    return () => unsubscribeMessages();
  }, [selectedThreadId, currentUserId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (threadMessages.length > 0 && messageDropdownOpen) {
      scrollToBottom();
    }
  }, [threadMessages, messageDropdownOpen, scrollToBottom]);

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  const handleOpenSettings = () => {
    setDropdownOpen(false);
    navigate('/settings');
  };

  const handleOpenMessages = async () => {
    setMessageStatus('');
    const nextOpen = !messageDropdownOpen;
    setMessageDropdownOpen(nextOpen);

    if (nextOpen && currentUserId && selectedThreadId) {
      await markThreadRead({ threadId: selectedThreadId, userId: currentUserId }).catch(() => {});
    }

    if (nextOpen && !selectedThreadId && messageThreads.length > 0) {
      setSelectedThreadId(messageThreads[0].id);
    }
  };

  const handleStartParentConversation = async () => {
    if (!parentProfile) {
      setMessageStatus('Parent profile is not linked yet.');
      return;
    }

    try {
      setStartingConversation(true);
      setMessageStatus('');
      const thread = await getOrCreateParentThread({ parentProfile });
      setSelectedThreadId(thread.id);
      setMessageStatus('');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      setMessageStatus('Could not start a conversation. Please try again.');
    } finally {
      setStartingConversation(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedThreadId || !currentUserId || !messageDraft.trim()) {
      return;
    }

    try {
      await sendThreadMessage({
        threadId: selectedThreadId,
        text: messageDraft,
        sender: {
          id: currentUserId,
          name:
            `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`.trim() ||
            userInfo?.displayName ||
            auth.currentUser?.email ||
            'User',
          role: userInfo?.role || 'user'
        }
      });
      setMessageDraft('');
    } catch (error) {
      setMessageStatus('Failed to send message.');
    }
  };

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        <div className={styles.logoContainer}>
          <img 
            src="/favicon.svg" 
            alt="Andama AI Logo" 
            className={styles.logo}
          />
          <span className={styles.logoText}>Andama AI</span>
        </div>
      </div>
      <div className={styles.right}>
        <div className={styles.profileWrapper} ref={messageRef}>
          <button
            ref={messageButtonRef}
            className={styles.iconBtn}
            aria-label="Messages"
            onClick={handleOpenMessages}
            style={{ position: 'relative' }}
          >
            <FaEnvelope />
            {messageUnreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {messageUnreadCount > 9 ? '9+' : messageUnreadCount}
              </span>
            )}
          </button>
          {messageDropdownOpen && (() => {
            const buttonRect = messageButtonRef.current?.getBoundingClientRect();
            const top = buttonRect ? buttonRect.bottom + 8 : 80;
            const right = buttonRect ? window.innerWidth - buttonRect.right : 32;

            return (
              <div
                className={styles.dropdown}
                style={{
                  minWidth: '620px',
                  maxWidth: '760px',
                  height: '460px',
                  position: 'fixed',
                  top: `${top}px`,
                  right: `${right}px`,
                  zIndex: 60,
                  padding: 0,
                  overflow: 'hidden'
                }}
              >
                <div style={{ display: 'flex', height: '100%' }}>
                  <div style={{ width: '36%', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Messages</span>
                      <button
                        onClick={() => { setMessageDropdownOpen(false); navigate('/messages'); }}
                        title="Open full messages page"
                        style={{ background: 'none', border: 'none', color: '#00E0E0', cursor: 'pointer', padding: '2px 4px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <FaExpandAlt style={{ fontSize: '0.7rem' }} /> Expand
                      </button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {messageThreads.length === 0 ? (
                        <div style={{ padding: '16px', color: '#9ca3af', fontSize: '0.9rem' }}>
                          {userInfo?.role === 'parent' ? (
                            <>
                              No conversations yet.
                              <div style={{ marginTop: '10px' }}>
                                <button
                                  onClick={handleStartParentConversation}
                                  disabled={startingConversation}
                                  style={{
                                    background: startingConversation ? '#6b7280' : '#00b8b8',
                                    border: 'none', color: '#fff', borderRadius: 8,
                                    padding: '8px 10px', cursor: startingConversation ? 'wait' : 'pointer',
                                    width: '100%', opacity: startingConversation ? 0.7 : 1
                                  }}
                                >
                                  {startingConversation ? 'Starting...' : 'Start conversation'}
                                </button>
                              </div>
                              {messageStatus && (
                                <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#fca5a5' }}>
                                  {messageStatus}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              No parent messages yet.
                              <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                                Conversations will appear here when parents reach out.
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        messageThreads.map((thread) => {
                          const threadUnread = thread.unreadBy?.[currentUserId] || 0;
                          return (
                            <button
                              key={thread.id}
                              onClick={() => setSelectedThreadId(thread.id)}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: thread.id === selectedThreadId ? 'rgba(0,224,224,0.12)' : 'transparent',
                                color: '#e5e7eb',
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid rgba(255,255,255,0.06)'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{thread.parentName || thread.subject || 'Conversation'}</div>
                                {threadUnread > 0 && (
                                  <span style={{
                                    background: '#00E0E0', color: '#111', borderRadius: '50%',
                                    width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.7rem', fontWeight: 'bold', flexShrink: 0
                                  }}>
                                    {threadUnread > 9 ? '9+' : threadUnread}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {thread.lastMessage || 'No messages yet'}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>
                                {formatRelativeTime(thread.lastMessageAt)}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div style={{ width: '64%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 'bold' }}>
                      Conversation
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                      {threadMessages.length === 0 ? (
                        <div style={{ color: '#9ca3af', textAlign: 'center', marginTop: 40 }}>
                          {selectedThreadId ? 'No messages yet. Start the conversation below.' : 'Select a conversation to view messages.'}
                        </div>
                      ) : (
                        threadMessages.map((message) => {
                          const isOwnMessage = message.senderId === currentUserId;
                          const badge = roleBadgeColor(message.senderRole);
                          return (
                            <div
                              key={message.id}
                              style={{
                                marginBottom: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isOwnMessage ? 'flex-end' : 'flex-start'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{message.senderName}</span>
                                <span style={{
                                  fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4,
                                  background: badge.bg, color: badge.text, fontWeight: 600, textTransform: 'capitalize'
                                }}>
                                  {message.senderRole || 'user'}
                                </span>
                              </div>
                              <div style={{
                                color: '#e5e7eb', fontSize: '0.9rem',
                                background: isOwnMessage ? 'rgba(0,184,184,0.18)' : 'rgba(255,255,255,0.06)',
                                borderRadius: 10,
                                padding: '8px 12px',
                                maxWidth: '85%',
                                wordBreak: 'break-word'
                              }}>
                                {message.text}
                              </div>
                              <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: 2 }}>
                                {formatRelativeTime(message.createdAt)}
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    {selectedThreadId && (
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px', display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          value={messageDraft}
                          onChange={(event) => setMessageDraft(event.target.value)}
                          placeholder="Type a message..."
                          style={{
                            flex: 1,
                            background: '#111827',
                            color: '#e5e7eb',
                            border: '1px solid rgba(255,255,255,0.15)',
                            borderRadius: 8,
                            padding: '8px 10px'
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />
                        <button
                          onClick={handleSendMessage}
                          style={{
                            background: '#00b8b8',
                            border: 'none',
                            color: '#fff',
                            borderRadius: 8,
                            padding: '8px 12px',
                            cursor: 'pointer'
                          }}
                        >
                          Send
                        </button>
                      </div>
                    )}
                    {messageStatus && (
                      <div style={{ padding: '0 10px 10px', color: '#fca5a5', fontSize: '0.8rem' }}>{messageStatus}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
        <div className={styles.profileWrapper} ref={notificationRef}>
          <button 
            ref={notificationButtonRef}
            className={styles.iconBtn} 
            aria-label="Alerts"
            onClick={() => {
              setNotificationDropdownOpen(!notificationDropdownOpen);
              if (!notificationDropdownOpen && unreadCount > 0) {
                setUnreadCount(0); // Mark as read when opening
              }
            }}
            style={{ position: 'relative' }}
          >
            <FaBell />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {notificationDropdownOpen && (() => {
            // Calculate position based on button location
            const buttonRect = notificationButtonRef.current?.getBoundingClientRect();
            const top = buttonRect ? buttonRect.bottom + 8 : 80;
            const right = buttonRect ? window.innerWidth - buttonRect.right : 32;
            
            return (
            <div 
              className={styles.dropdown} 
              style={{ 
                minWidth: '320px', 
                maxWidth: '400px', 
                maxHeight: '400px', 
                overflowY: 'auto',
                position: 'fixed',
                top: `${top}px`,
                right: `${right}px`,
                zIndex: 60
              }}
            >
              <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontWeight: 'bold' }}>
                Notifications
              </div>
              {notifications.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  No notifications
                </div>
              ) : (
                notifications.map((notif, index) => (
                  <div 
                    key={index}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px'
                    }}
                  >
                    <div style={{ marginTop: '2px' }}>
                      {notif.type === 'warning' ? (
                        <FaExclamationTriangle style={{ color: '#fbbf24' }} />
                      ) : (
                        <FaCheckCircle style={{ color: '#10b981' }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{notif.title}</div>
                      <div style={{ fontSize: '0.85rem', color: '#ccc', marginBottom: '4px' }}>{notif.message}</div>
                      <div style={{ fontSize: '0.75rem', color: '#999' }}>
                        {new Date(notif.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {notifications.length > 0 && (
                <div 
                  style={{ 
                    padding: '12px', 
                    borderTop: '1px solid rgba(255,255,255,0.1)', 
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setNotifications([]);
                    setUnreadCount(0);
                    setNotificationDropdownOpen(false);
                  }}
                >
                  Clear all
                </div>
              )}
            </div>
            );
          })()}
        </div>
        <div className={styles.profileWrapper} ref={dropdownRef}>
          <button
            className={styles.profilePill}
            onClick={() => setDropdownOpen((open) => !open)}
            aria-label="Account"
          >
            <div className={styles.profileImageContainer}>
              {userInfo?.profilePicture ? (
                <img 
                  src={userInfo.profilePicture} 
                  alt="Profile" 
                  className={styles.profileImage}
                />
              ) : (
                <div className={styles.profileInitials}>
                  {userInfo 
                    ? ((userInfo.firstName?.[0] || '') + (userInfo.lastName?.[0] || ''))
                    : (auth.currentUser?.email?.[0]?.toUpperCase() || 'U')
                  }
                </div>
              )}
            </div>
            <div className={styles.profileText}>
              <span className={styles.userName}>
                {userInfo 
                  ? `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || userInfo.displayName || userInfo.email?.split('@')[0] || 'User'
                  : auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'User'
                }
              </span>
              <span className={styles.userRole}>
                {userInfo?.role || 'user'}
              </span>
            </div>
            <FaChevronDown className={styles.chevronIcon} />
          </button>
          {dropdownOpen && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownItem} onClick={handleOpenSettings} style={{ cursor: 'pointer' }}>
                <FaCog className={styles.dropdownIcon} /> Settings
              </div>
              <div className={styles.dropdownItem} onClick={handleLogout} style={{ cursor: 'pointer' }}>
                <FaSignOutAlt className={styles.dropdownIcon} /> Logout
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar; 